import { useState } from 'react';
import {
  PLAN_BASE_CENTS,
  PLAN_INCLUDED_SEATS,
  PLAN_SEAT_CENTS,
  billableSeats,
  formatCents,
  monthlyTotalCents,
} from '@fsm/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from './ui';

/**
 * What the team currently costs, on the page where the team is changed.
 *
 * ## Why it is here and not on a billing page of its own
 *
 * Because this is where the number changes. Adding a technician raises the
 * monthly bill, and the moment somebody needs to know that is the moment
 * before they click "Add someone" — not later, in a different tab, next to an
 * invoice they are already annoyed about.
 *
 * A subscription that grows silently is the single most common reason a small
 * business charges a SaaS bill back, and the charge-back costs more than the
 * seat. Saying the number out loud is cheaper than being right about it
 * afterwards.
 *
 * ## The arithmetic is done here, not fetched
 *
 * Seats are active users, the prices are constants in @fsm/shared, and the
 * server computes the quantity from exactly the same count when it talks to
 * Stripe. Adding an endpoint for a sum that both sides already know would be
 * a second source of truth, and the whole point is that there is one.
 */
export function SeatBilling({ activeUsers, canManage }: { activeUsers: number; canManage: boolean }) {
  const { company } = useAuth();
  const [opening, setOpening] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const extra = billableSeats(activeUsers);
  const total = monthlyTotalCents(activeUsers);

  /*
   * Only for companies that are actually paying. A trial has no subscription
   * to manage and no bill to show, and quoting one at somebody who has not
   * decided yet reads as a demand.
   */
  const subscribed = company?.access?.reason === 'subscribed';

  return (
    <div className="border-b border-line bg-raised px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div>
          <div className="flex items-baseline gap-1.5">
            <span className="tnum font-display text-[19px] font-extrabold tracking-[-0.03em] text-ink">
              {formatCents(total)}
            </span>
            <span className="text-[13px] text-ink-3">a month</span>
          </div>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            {activeUsers} {activeUsers === 1 ? 'user' : 'users'} — {PLAN_INCLUDED_SEATS} included
            {extra > 0
              ? `, ${extra} extra at ${formatCents(PLAN_SEAT_CENTS)} each`
              : `. The next ${PLAN_INCLUDED_SEATS - activeUsers === 1 ? 'person costs' : 'people cost'} nothing`}
          </p>
        </div>

        {canManage && subscribed && (
          <Button
            size="sm"
            variant="secondary"
            loading={opening}
            onClick={() => {
              setOpening(true);
              setFailed(null);
              /*
               * Stripe's own portal: card, invoices, cancellation. Worth the
               * one button from the first customer — without it every expired
               * card is an email, and every cancellation is a conversation
               * somebody resents having to have.
               */
              api.billing
                .portal()
                .then(({ url }) => {
                  window.location.href = url;
                })
                .catch((error: unknown) => {
                  setOpening(false);
                  setFailed(
                    error instanceof Error && error.message
                      ? error.message
                      : 'Could not open the billing page.',
                  );
                });
            }}
          >
            Manage billing
          </Button>
        )}
      </div>

      {failed && <p className="mt-2 text-[12.5px] text-danger">{failed}</p>}
    </div>
  );
}

/**
 * The one line on the add-someone form.
 *
 * Deliberately states the consequence rather than the rule: "this adds $25 a
 * month" is a fact about the next invoice, where "extra users are $25" is a
 * policy somebody has to apply to their own situation while filling in a
 * form. It also says *when*, because a bill people expect today and receive
 * on the 1st is a bill they query.
 */
export function SeatCostNote({ activeUsers }: { activeUsers: number }) {
  const free = PLAN_INCLUDED_SEATS - activeUsers;

  if (free > 0) {
    return (
      <p className="text-[13px] text-ink-3">
        Free — your plan includes {PLAN_INCLUDED_SEATS} users and you are using {activeUsers}.
      </p>
    );
  }

  return (
    <p className="text-[13px] text-ink-3">
      This adds <span className="font-semibold text-ink">{formatCents(PLAN_SEAT_CENTS)} a month</span>{' '}
      to your subscription, from {formatCents(monthlyTotalCents(activeUsers))} to{' '}
      {formatCents(monthlyTotalCents(activeUsers + 1))}. The part-month is added to your next
      invoice, not charged now.
    </p>
  );
}

/** Exported for the harness, which renders these without an auth context. */
export const SEAT_PRICES = { PLAN_BASE_CENTS, PLAN_SEAT_CENTS, PLAN_INCLUDED_SEATS };
